import React from "react";
import { ArrowLeft, ArrowRight, CheckCircle, Clock, MapPin } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useServiceContent } from "../hooks/use-service-content";
import { useSectionsContent } from "../hooks/use-sections-content";
import { getServiceSlug } from "../lib/service-utils";
import { useLanguage } from "../i18n/LanguageContext";

const iconByName = {
  Brain: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  ),
  Heart: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  ),
  Users: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
};

const ServiceDetail = () => {
  const { serviceSlug = "" } = useParams();
  const { services } = useServiceContent();
  const { sectionsContent } = useSectionsContent();
  const { tr, localizeText } = useLanguage();
  const copy = sectionsContent?.publicPageCopy || {};
  const text = (key, fallback) => copy[key] || fallback;
  const activeServices = Array.isArray(services)
    ? services.filter((item) => item?.isActive !== false)
    : [];

  const service = activeServices.find(
    (item) => getServiceSlug(item, localizeText) === serviceSlug,
  );

  if (!service) {
    return (
      <div className="min-h-screen bg-sage-50 py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <Card className="border-sage-200 bg-white">
            <CardContent className="p-8 text-center space-y-5">
              <h1 className="text-3xl font-bold text-sage-900">{text('serviceDetailNotFound', 'Service not found')}</h1>
              <p className="text-sage-700">
                The requested service detail page is unavailable.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/services">
                  <Button variant="outline" className="border-sage-300 text-sage-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Services
                  </Button>
                </Link>
                <Link to="/booking">
                  <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                    {tr("action.bookSession")}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-sage-600 to-earth-700 text-white py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <Link
            to="/services"
            className="inline-flex items-center text-sage-100 hover:text-white mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {tr("nav.services")}
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              {iconByName[service.icon] || iconByName.Brain}
            </div>
            <Badge className="bg-white/20 text-white hover:bg-white/30">
              {tr("services.badge.professionalService")}
            </Badge>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-5">
            {localizeText(service.title)}
          </h1>
          <p className="text-xl text-sage-100 max-w-3xl leading-relaxed">
            {localizeText(service.shortDescription)}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={service.image}
                alt={localizeText(service.title)}
                className="w-full h-96 object-cover"
              />
            </div>

            <Card className="border-sage-200 bg-sage-50">
              <CardContent className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-sage-600" />
                      <p className="font-semibold text-sage-900">{tr("services.duration")}</p>
                    </div>
                    <p className="text-sm text-sage-700">{localizeText(service.duration)}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-sage-600" />
                      <p className="font-semibold text-sage-900">{tr("services.delivery")}</p>
                    </div>
                    <div className="space-y-1">
                      {(Array.isArray(service.deliveryMethods)
                        ? service.deliveryMethods
                        : []
                      ).map((method, index) => (
                        <p key={`delivery-${index}`} className="text-sm text-sage-700">
                          {localizeText(method)}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-sage-900 mb-3">{text('serviceDetailOverview', 'Overview')}</h2>
              <p className="text-sage-700 leading-relaxed">
                {localizeText(service.fullDescription)}
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-sage-900 mb-3">
                {tr("services.keyBenefits")}
              </h3>
              <ul className="space-y-2">
                {(Array.isArray(service.benefits) ? service.benefits : []).map(
                  (benefit, index) => (
                    <li
                      key={`benefit-${index}`}
                      className="flex items-start gap-2 text-sage-700"
                    >
                      <CheckCircle className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                      <span>{localizeText(benefit)}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>

            <Card className="border-sage-200 bg-white shadow-sm">
              <CardContent className="p-6">
                <h4 className="font-semibold text-sage-900 mb-1">{text('serviceDetailPricing', 'Pricing')}</h4>
                <p className="text-sage-700 mb-5">{localizeText(service.pricing)}</p>
                <Link to={`/booking?service=${encodeURIComponent(serviceSlug)}`}>
                  <Button className="w-full bg-sage-600 hover:bg-sage-700 text-white group">
                    {tr("services.bookThisService")}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
